import { InsuranceUsage } from '@typesApp/ClaimData'
import { ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { InsuranceMemberStep } from '@typesApp/InsuranceMember'
import { InsurancePlan, InsurancePlanBenefitItem } from '@typesApp/InsurancePlan'

const MEMBER_FOUND_STATUS_LIST = [
  ClaimRecordStatus.INSURANCE_MEMBER_FOUND,
  ClaimRecordStatus.READY_TO_CLAIM,
  ClaimRecordStatus.READY_TO_QUOTE,
  ClaimRecordStatus.CLAIM_ACCEPTED,
  ClaimRecordStatus.QUOTE_REQUIRED,
  ClaimRecordStatus.NO_BENEFITS_AVAILABLE,
]

const MEMBER_NOT_FOUND_STATUS_LIST = [
  ClaimRecordStatus.INSURANCE_MEMBER_NOT_FOUND,
  ClaimRecordStatus.MULTIPLE_INSURANCE_MEMBERS_FOUND,
]

export const isInsuranceMemberNoBenefitsAvailable = (status: ClaimRecordStatus | undefined): boolean => {
  return status === ClaimRecordStatus.NO_BENEFITS_AVAILABLE
}

export const isInsuranceMemberFoundStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status ? MEMBER_FOUND_STATUS_LIST.includes(status) : false
}

export const isInsuranceMemberNotFoundStatus = (status: ClaimRecordStatus | undefined): boolean => {
  return status ? MEMBER_NOT_FOUND_STATUS_LIST.includes(status) : false
}

export const isInsuranceUsageEnabled = (insuranceUsage: InsuranceUsage | undefined): boolean => {
  return insuranceUsage === InsuranceUsage.INSURANCE_ENABLED
}

export const isInsuranceUsageDisabled = (usageStatus: InsuranceUsage | undefined): boolean => {
  return usageStatus === InsuranceUsage.INSURANCE_DISABLED
}

export const determineInsuranceMemberStep = (claimRecordStatus: ClaimRecordStatus | undefined) => {
  if (!claimRecordStatus) return InsuranceMemberStep.FIND_MEMBER

  switch (claimRecordStatus) {
    case ClaimRecordStatus.INSURANCE_MEMBER_FOUND:
    case ClaimRecordStatus.NO_BENEFITS_AVAILABLE:
    case ClaimRecordStatus.READY_TO_QUOTE:
    case ClaimRecordStatus.READY_TO_CLAIM:
    case ClaimRecordStatus.CLAIM_ACCEPTED:
    case ClaimRecordStatus.QUOTE_REQUIRED:
      return InsuranceMemberStep.MEMBER_FOUND
    case ClaimRecordStatus.QUOTE_REQUESTED:
      return InsuranceMemberStep.SYNC_INSURANCE
    case ClaimRecordStatus.RESTRICTED:
      return InsuranceMemberStep.RESTRICTED
    case ClaimRecordStatus.INSURANCE_MEMBER_NOT_FOUND:
    case ClaimRecordStatus.INSURANCE_MEMBER_REQUIRED:
    case ClaimRecordStatus.MULTIPLE_INSURANCE_MEMBERS_FOUND:
    default:
      return InsuranceMemberStep.FIND_MEMBER
  }
}

export const getInsuranceMemberPlanActiveBenefit = (plan: InsurancePlan): InsurancePlanBenefitItem | undefined => {
  const planBenefits = (plan?.planBenefits ?? []).filter(isValidBenefit)

  const routinePlanBenefit = planBenefits.find(
    benefit => benefit.benefitCategory === 'ROUTINE' && benefit.benefitStatus === 'BENEFITS_AVAILABLE'
  )
  const additionalPlanBenefit = planBenefits.find(
    benefit => benefit.benefitCategory === 'ADDITIONAL_PURCHASE' && benefit.benefitStatus === 'BENEFITS_AVAILABLE'
  )
  const isBenefitAvailable = !!(routinePlanBenefit ?? additionalPlanBenefit)
  let activeBenefit = routinePlanBenefit ?? additionalPlanBenefit

  if (!isBenefitAvailable) {
    const routineFutureBenefit = planBenefits.find(
      benefit => benefit.benefitCategory === 'ROUTINE' && benefit.benefitStatus === 'BENEFITS_NOT_AVAILABLE'
    )
    const additionalFutureBenefit = planBenefits.find(
      benefit => benefit.benefitCategory === 'ADDITIONAL_PURCHASE' && benefit.benefitStatus === 'BENEFITS_NOT_AVAILABLE'
    )
    activeBenefit = routineFutureBenefit ?? additionalFutureBenefit
  }

  return activeBenefit
}

const isValidBenefit = (benefit: InsurancePlanBenefitItem): boolean => {
  return benefit.benefitAvailability === 'SUPPORTED' || benefit.benefitAvailability === 'COMING_SOON'
}
